// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Accuracy section of the info panel, and the error-budget
 * table the My data job page shows at review (docs/AYNI_DATA_INTAKE_PLAN.md,
 * "Transparency about errors and tolerances → Where people see it").
 * Shown for any release whose descriptor carries an error budget: own data
 * now, public streams once the stream host publishes the fields (O9).
 * Without one the panel has no Accuracy section and values keep the
 * rounding step as before.
 *
 * Built with DOM nodes and text only: every string in a budget comes from
 * a file someone uploaded or a processor's report.
 */

import { t } from '../i18n'
import { budgetRows, budgetsDiffer, formatWithUncertainty, toleranceVerdict, videoUncertainty, type ErrorBudget } from '../services/errorBudget'
import type { ReleaseEncoding } from '../types/release-encoding'
import { sourceCodeStep, sourceEncoding } from '../services/decoderRange'
import { codecOfRelease, codecP99Codes, fidelityOf, frameCustodyAt, hasFrameCustody, logCodeRatio, readingUncertainty } from '../services/valueFidelity'
import type { FrameCustody } from '../types/release-fidelity'
import { formatDate, formatNumber } from '../i18n/format'

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

/** The budget as a two-column table: the error source, and what was measured. */
export function budgetTable(budget: ErrorBudget, units: string | null): HTMLTableElement {
  const table = el('table', 'accuracy-table')
  const caption = el('caption', 'sr-only', t('accuracy.table.caption'))
  table.append(caption)
  const body = el('tbody')
  for (const row of budgetRows(budget, units)) {
    const tr = el('tr')
    tr.dataset.row = row.key
    const th = el('th', undefined, row.label)
    th.scope = 'row'
    tr.append(th, el('td', undefined, row.text))
    body.append(tr)
  }
  table.append(body)
  return table
}

/**
 * A streaming difference of `steps` steps as people read it: in the
 * release's units on a linear scale, a share of the value on a log one
 * (ratio^steps − 1, the larger side), and as steps for classes. The panel
 * never names the codec, its setting or "codes": how values are carried
 * is ours (owner, 2026-11-03), the error in the data's own units is what
 * a reader needs.
 */
export function stepsAsAmount(enc: ReleaseEncoding, steps: number): string {
  const src = sourceEncoding(enc)
  if (src.kind === 'luma8-log') {
    const ratio = logCodeRatio(src)
    if (ratio !== null) return formatNumber(Math.pow(ratio, steps) - 1, { style: 'percent', maximumSignificantDigits: 2 })
  } else if (src.kind !== 'luma8-classified') {
    const step = Math.abs(sourceCodeStep(src))
    if (Number.isFinite(step) && step > 0) {
      const text = formatNumber(steps * step, { maximumSignificantDigits: 3 })
      return src.units ? t('probe.value', { value: text, units: src.units }) : text
    }
  }
  return t('accuracy.codec.steps', { n: formatNumber(steps) })
}

/**
 * The rows a public release's section shows: its source, the release,
 * rounding, streaming, regridding and clipping, as far as the release
 * states them (valueFidelity.ts; value error audit, 2026-10-19).
 */
export function fidelityRows(enc: ReleaseEncoding): { key: string; label: string; text: string }[] {
  const src = sourceEncoding(enc)
  const known = fidelityOf(enc)
  const f = known.release
  const rows: { key: string; label: string; text: string }[] = []
  const s = known.source
  if (s) {
    const what = [s.product ?? s.productId, s.version, s.variable, s.doi ? `doi:${s.doi}` : null].filter(Boolean).join(' · ')
    const how = s.distributedAs === 'classes'
      ? t('accuracy.source.classes', { how: t(s.classValue === 'midpoint' ? 'accuracy.source.classValue.midpoint' : 'accuracy.source.classValue.other') })
      : s.distributedAs === 'image' ? t('accuracy.source.image') : null
    if (what || how) rows.push({ key: 'source', label: t('accuracy.row.source'), text: [what, how].filter(Boolean).join(' ') })
  }
  if (f?.releaseId) {
    const published = f.publishedAt ? Date.parse(f.publishedAt) : NaN
    const date = Number.isFinite(published) ? formatDate(new Date(published), { dateStyle: 'medium', timeStyle: 'short' }) : '—'
    rows.push({ key: 'release', label: t('accuracy.row.release'), text: t('accuracy.release.text', { id: f.releaseId, date }) })
  }
  const units = src.units
  const amount = (v: number): string => {
    const text = formatNumber(v, { maximumSignificantDigits: 3 })
    return units ? t('probe.value', { value: text, units }) : text
  }
  if (src.kind === 'luma8-classified') {
    rows.push({ key: 'quantisation', label: t('accuracy.row.quantisation'), text: t('accuracy.quantisationClasses') })
  } else if (src.kind === 'luma8-log') {
    const ratio = logCodeRatio(src)
    if (ratio !== null) {
      const percent = formatNumber(ratio - 1, { style: 'percent', maximumSignificantDigits: 2 })
      rows.push({ key: 'quantisation', label: t('accuracy.row.quantisation'), text: t('accuracy.quantisationRelative', { percent }) })
    }
  } else {
    const step = Math.abs(sourceCodeStep(src))
    rows.push({ key: 'quantisation', label: t('accuracy.row.quantisation'), text: t('accuracy.quantisation', { step: amount(step), half: amount(step / 2) }) })
  }
  const codec = codecOfRelease(enc)
  const p99 = codecP99Codes(codec)
  const maxCodes = codec?.codes?.max
  const max = maxCodes !== null && maxCodes !== undefined ? stepsAsAmount(enc, maxCodes) : '—'
  let codecText: string
  if (!p99) codecText = t('accuracy.codec.unpublished')
  else if (p99.basis === 'lossless') codecText = t('accuracy.codec.lossless')
  else if (p99.basis === 'bound') codecText = t('accuracy.codec.bound', { max })
  else codecText = t('accuracy.codec.measured', { p99: stepsAsAmount(enc, p99.codes), max })
  rows.push({ key: 'codec', label: t('accuracy.row.codec'), text: codecText })
  const rg = known.regrid
  if (rg?.method || rg?.text) {
    const method = rg.method ?? rg.text ?? ''
    const p = rg.vsNative?.p99
    rows.push({ key: 'regrid', label: t('accuracy.row.regrid'), text: p !== null && p !== undefined ? t('accuracy.regridNative', { method, p99: amount(p) }) : method })
  }
  const cl = known.clipping
  const share = cl ? (cl.share ?? (cl.lowShare ?? 0) + (cl.highShare ?? 0)) : null
  if (share !== null && share > 0) {
    rows.push({ key: 'clipping', label: t('accuracy.row.clipping'), text: t('accuracy.clippingShare', { share: formatNumber(share, { style: 'percent', maximumSignificantDigits: 2 }) }) })
  }
  return rows
}

/** The frame's record in words: its time, run, source files and largest streaming difference (in the release's units when `enc` is given). */
export function frameCustodyText(entry: FrameCustody | null, enc: ReleaseEncoding | null = null): string {
  if (!entry) return t('accuracy.frame.none')
  const time = entry.time ?? entry.validTime ?? String(entry.index)
  const parts = [entry.run ? t('accuracy.frame.run', { time, run: entry.run }) : t('accuracy.frame.time', { time })]
  if (entry.files.length) parts.push(t('accuracy.frame.files', { files: entry.files.map((f) => f.url.split('/').pop() ?? f.url).join(', ') }))
  if (entry.maxCodeError !== null) parts.push(t('accuracy.frame.codec', { max: enc ? stepsAsAmount(enc, entry.maxCodeError) : t('accuracy.codec.steps', { n: formatNumber(entry.maxCodeError) }) }))
  return parts.join('; ')
}

/**
 * The info panel's Accuracy section. A release with an error budget (own
 * data) shows the budget table, as before; every other value-encoded
 * release shows what it states about its source, rounding, streaming,
 * regridding and clipping, and what it does not (value error audit,
 * 2026-10-19). `reviewed` is the budget the processor reported when the
 * owner reviewed it (own data): if the published descriptor says something
 * else, the descriptor's figures are shown and the difference is flagged.
 * `frameTimeMs`, when given, fills the current frame's record.
 */
export function accuracySection(enc: ReleaseEncoding | undefined, reviewed: ErrorBudget | null = null, frameTimeMs: number | null = null): HTMLElement | null {
  if (!enc) return null
  // Version 1 budgets (own data) keep their table; version 2 (codec error in codes, proposed for every release) reads through valueFidelity.
  const acc = enc.accuracy && enc.accuracy.budget.version !== '2' ? enc.accuracy : undefined
  const section = el('section', 'info-accuracy')
  section.setAttribute('aria-label', t('accuracy.title'))
  section.append(el('p', 'info-section-label', t('accuracy.title')))
  if (acc) {
    const units = acc.budget.units ?? enc.units
    section.append(el('p', 'info-accuracy-intro', t('accuracy.intro')))
    if (budgetsDiffer(acc.budget, reviewed)) section.append(el('p', 'info-accuracy-flag', t('accuracy.differs')))
    const step = sourceCodeStep(enc)
    const u = enc.kind === 'luma8-linear' ? videoUncertainty(acc, step) : null
    if (u !== null) {
      section.append(el('p', 'info-accuracy-value', t('accuracy.valueRule', { example: formatWithUncertainty(enc.vmin + (enc.vmax - enc.vmin) / 2, u, units) })))
    }
    section.append(budgetTable(acc.budget, units))
    if (acc.tolerance) {
      section.append(el('p', `info-accuracy-tolerance${acc.tolerance.pass === false ? ' is-fail' : ''}`, toleranceVerdict(acc.tolerance, acc.tolerance.pass, units)))
    }
    section.append(el('p', 'info-accuracy-exact', t(acc.exact ? 'accuracy.exact.stored' : 'accuracy.exact.none')))
    return section
  }
  section.append(el('p', 'info-accuracy-intro', t('accuracy.publicIntro')))
  const src = sourceEncoding(enc)
  if (src.kind !== 'luma8-classified') {
    const mid = src.kind === 'luma8-log' ? Math.sqrt(src.vmin * src.vmax) : src.vmin + (src.vmax - src.vmin) / 2
    const u = readingUncertainty(enc, mid)
    if (u) {
      const example = formatWithUncertainty(mid, u.u, src.units)
      section.append(el('p', 'info-accuracy-value', u.complete ? t('accuracy.valueRule', { example }) : t('accuracy.valueRuleRoundingOnly', { example })))
    }
  }
  const table = el('table', 'accuracy-table')
  table.append(el('caption', 'sr-only', t('accuracy.table.caption')))
  const body = el('tbody')
  const addRow = (key: string, label: string, text: string): HTMLTableCellElement => {
    const tr = el('tr')
    tr.dataset.row = key
    const th = el('th', undefined, label)
    th.scope = 'row'
    const td = el('td', undefined, text)
    tr.append(th, td)
    body.append(tr)
    return td
  }
  for (const row of fidelityRows(enc)) addRow(row.key, row.label, row.text)
  if (frameTimeMs !== null && hasFrameCustody(enc)) {
    const cell = addRow('frame', t('accuracy.row.frame'), t('accuracy.frame.loading'))
    void frameCustodyAt(enc, frameTimeMs).then((entry) => {
      cell.textContent = frameCustodyText(entry, enc)
    })
  }
  table.append(body)
  section.append(table)
  return section
}
